import { motion, AnimatePresence } from "motion/react";
import { X, Download, CheckCircle2 } from "lucide-react";

interface AdmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AdmissionModal({ isOpen, onClose }: AdmissionModalProps) {
  const handleDownload = () => {
    // In a real application, this would trigger a download of the actual PDF form
    alert("Admission form download started! In a live website, this would download the PDF application form.");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative max-w-2xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 sm:p-8 text-white relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors z-10"
              >
                <X className="size-6" />
              </button>
              <h2 className="text-2xl sm:text-3xl md:text-4xl mb-2 pr-12">Admission Application</h2>
              <p className="text-white/90 text-sm sm:text-base">Join the Uckfield Family Today!</p>
            </div>

            {/* Content */}
            <div className="p-6 sm:p-8 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div className="mb-8">
                <div className="inline-flex items-center bg-green-50 text-green-700 px-4 py-2 rounded-full mb-6">
                  <CheckCircle2 className="size-5 mr-2" />
                  <span>Applications Now Open</span>
                </div>
                
                <h3 className="text-2xl text-gray-900 mb-4">How to Apply</h3>
                <ol className="space-y-4 text-gray-700">
                  <li className="flex items-start">
                    <span className="bg-blue-500 text-white rounded-full size-7 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">1</span>
                    <span>Download the admission form by clicking the button below</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-500 text-white rounded-full size-7 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">2</span>
                    <span>Fill out the form completely with accurate information</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-500 text-white rounded-full size-7 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">3</span>
                    <span>Attach required documents (birth certificate, passport photographs, previous school report)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-500 text-white rounded-full size-7 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">4</span>
                    <span>Submit the form in person at our school or via email to uckfieldschool@gmail.com</span>
                  </li>
                </ol>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                <h4 className="text-lg text-gray-900 mb-2">Required Documents:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center">
                    <span className="text-blue-500 mr-2">✓</span>
                    Completed admission form
                  </li>
                  <li className="flex items-center">
                    <span className="text-blue-500 mr-2">✓</span>
                    Child's birth certificate (photocopy)
                  </li>
                  <li className="flex items-center">
                    <span className="text-blue-500 mr-2">✓</span>
                    2 passport photographs
                  </li>
                  <li className="flex items-center">
                    <span className="text-blue-500 mr-2">✓</span>
                    Previous school report (if applicable)
                  </li>
                </ul>
              </div>

              {/* Download Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleDownload}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center space-x-2"
              >
                <Download className="size-5" />
                <span>Download Admission Form</span>
              </motion.button>

              <p className="text-center text-sm text-gray-500 mt-4">
                Need help? Call us at <a href="tel:+2347036853581" className="text-blue-600 hover:underline">07036853581</a> or visit our school office
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}